import { useState } from 'react'
import type { User } from '../../../utils/loginUtils'
import PopupError from '../../PopupForm/PopupError'
import { getMessage } from '../../../utils/messageUtils'
import {
  deleteUser,
  insertUser,
  updateUser,
  userChanged,
} from '../../../utils/usersUtils'
import Spinner from '../../Spinner/Spinner'
import type { ExtraButton } from '../../PopupForm/Popup'
import Popup from '../../PopupForm/Popup'
import UserPopupForm from './UserPopupForm'

interface UserPopupContentProps {
  currentUser: User
  user: User | null
  isMobile: boolean
  setPopupOnOk: React.Dispatch<React.SetStateAction<(() => void) | undefined>>
  closePopup: () => void
  reLoadData: () => void
  setPopupExtraButtons: (
    buttons: { label: string; onClick: () => void }[]
  ) => void
}

function UserPopupContent({
  currentUser,
  user,
  isMobile,
  setPopupOnOk,
  closePopup,
  reLoadData,
  setPopupExtraButtons,
}: UserPopupContentProps) {
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [name, setName] = useState(user?.username || '')
  const [password, setPassword] = useState('')
  const [chatId, setChatId] = useState(user?.telegram_chat_id || '')
  const [alerts, setAlerts] = useState<boolean>(user?.alerts === 1 || false)
  const [admin, setAdmin] = useState<boolean>(user?.admin === 1 || false)

  const [isLoading, setIsLoading] = useState(false)

  // Popup
  const [deletePopupOpen, setDeletePopupOpen] = useState(false)

  if (!setPopupOnOk || !closePopup || !reLoadData || !setPopupExtraButtons) {
    console.error('One or more required props are missing for UserPopupContent')
    return null
  }

  const saveNewUser = async () => {
    const newUser: User = {
      id: null,
      username: name,
      password,
      telegram_chat_id: chatId,
      alerts: alerts ? 1 : 0,
      admin: admin ? 1 : 0,
    }
    return (await insertUser(currentUser.id!, newUser)) as {
      success: boolean
      error?: string
    }
  }

  const updateExistingUser = async () => {
    const newUser: User = { ...user! }
    newUser.username = name.trim()
    newUser.password = password.trim()
    newUser.telegram_chat_id = chatId.trim()
    newUser.alerts = alerts ? 1 : 0
    newUser.admin = admin ? 1 : 0

    if (userChanged(user!, newUser)) {
      return (await updateUser(currentUser.id!, newUser)) as {
        success: boolean
        error?: string
      }
    } else {
      return undefined
    }
  }

  const clickDeleteUser = () => {
    setDeletePopupOpen(true)
  }

  const deleteExistingUser = async () => {
    setIsLoading(true)
    try {
      const result = (await deleteUser(currentUser.id!, user!.username)) as {
        success: boolean
        error?: string
      }
      if (!result.success) {
        setErrorMessage(getMessage('user_delete_error') + (result.error || ''))
      } else {
        reLoadData()
        closePopup()
      }
    } catch (error) {
      setErrorMessage(
        getMessage('user_delete_error') +
          (error instanceof Error ? error.message : '')
      )
    } finally {
      setIsLoading(false)
      setDeletePopupOpen(false)
    }
  }

  let extraButtons: ExtraButton[] = []
  if (user && user.username !== currentUser.username) {
    extraButtons = [
      {
        label: getMessage('delete'),
        className: 'popup-delete-button',
        onClick: user ? () => clickDeleteUser() : undefined,
      },
    ] as ExtraButton[]
  }
  setPopupExtraButtons(extraButtons)

  const checkUserInputs = () => {
    if (name.trim() === '') {
      setErrorMessage(getMessage('user_name_empty'))
      return false
    }
    if (!user && password.trim() === '') {
      setErrorMessage(getMessage('user_password_empty'))
      return false
    }
    return true
  }

  const saveUser = async () => {
    //Checks
    if (!checkUserInputs()) return

    setIsLoading(true)
    let result: { success: boolean; error?: string } | undefined
    if (!user) {
      result = await saveNewUser()
    } else {
      result = await updateExistingUser()
    }
    setIsLoading(false)
    if (result === undefined) {
      // No changes
      closePopup()
      return
    }
    if (!result.success) {
      if (result.error?.includes('UNIQUE constraint failed: users.username')) {
        setErrorMessage(getMessage('user_name_exists'))
      } else {
        setErrorMessage(getMessage('user_save_error') + (result.error || ''))
      }
    } else {
      reLoadData()
      closePopup()
    }
  }
  setPopupOnOk(() => saveUser)

  return (
    <div className={`pop-up-form ${isMobile ? 'pop-up-form-mobile' : ''}`}>
      <PopupError
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      <UserPopupForm
        currentUser={currentUser}
        creatingNew={!user}
        name={name}
        setName={setName}
        password={password}
        setPassword={setPassword}
        chatId={chatId}
        setChatId={setChatId}
        alerts={alerts}
        setAlerts={setAlerts}
        admin={admin}
        setAdmin={setAdmin}
        isMobile={isMobile}
      />
      <Popup
        isOpen={deletePopupOpen}
        isMobile={isMobile}
        message={getMessage('user_delete_confirm')}
        onOk={() => deleteExistingUser()}
        onClose={() => setDeletePopupOpen(false)}
      />
      <Spinner isOpen={isLoading} overlay={true} />
    </div>
  )
}

export default UserPopupContent
